import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../store';

const API = (() => {
  const h = window.location.hostname;
  return process.env.REACT_APP_API_URL || ((h === 'localhost' || h === '127.0.0.1') ? 'http://localhost:5001/api' : `http://${h}:5001/api`);
})();

interface Branch {
  id: string;
  name: string;
  city: string;
  code: string;
}

interface DriveReport {
  id: string;
  branch_id: string;
  branch_name?: string;
  branch_city?: string;
  report_date: string;
  before_drive_url?: string;
  after_drive_url?: string;
  notes?: string;
  submitted_by?: string;
  status: 'pending' | 'reviewed';
  reviewed_at?: string;
  review_notes?: string;
  created_at: string;
}

interface MediaFile {
  id: string;
  media_type: string;
  file_name: string;
  file_size: number;
  mime_type: string;
  created_at: string;
}

const today = new Date().toISOString().slice(0, 10);

export const BranchConfigPage: React.FC = () => {
  const { token } = useAuth();
  const authHeaders = { Authorization: `Bearer ${token}` };

  const [activeTab, setActiveTab] = useState<'laporan' | 'logo'>('laporan');
  const [branches, setBranches] = useState<Branch[]>([]);

  // Laporan tab state
  const [reports, setReports] = useState<DriveReport[]>([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [filterBranch, setFilterBranch] = useState('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'reviewed'>('all');
  const [filterDate, setFilterDate] = useState('');
  const [reviewModal, setReviewModal] = useState<DriveReport | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const [reviewError, setReviewError] = useState('');

  // Logo tab state
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    if (activeTab === 'laporan') fetchReports();
  }, [activeTab, filterBranch, filterStatus, filterDate]);

  useEffect(() => {
    if (activeTab === 'logo' && selectedBranchId) fetchMedia();
  }, [activeTab, selectedBranchId]);

  const fetchBranches = async () => {
    try {
      const res = await axios.get(`${API}/branches`, { headers: authHeaders });
      const list = Array.isArray(res.data) ? res.data : (res.data.data || []);
      setBranches(list);
      if (list.length > 0) setSelectedBranchId(list[0].id);
    } catch { setBranches([]); }
  };

  const fetchReports = async () => {
    setLoadingReports(true);
    try {
      const params: string[] = [];
      if (filterStatus !== 'all') params.push(`status=${filterStatus}`);
      if (filterBranch !== 'all') params.push(`branch_id=${filterBranch}`);
      // filter tanggal — kirim sebagai range (1 hari)
      if (filterDate) {
        params.push(`start_date=${filterDate}`);
        params.push(`end_date=${filterDate}`);
      }
      const url = `${API}/branch-config/drive-reports` + (params.length ? '?' + params.join('&') : '');
      const res = await axios.get(url, { headers: authHeaders });
      setReports(res.data.data || []);
    } catch { setReports([]); }
    finally { setLoadingReports(false); }
  };

  const handleReview = async (status: 'reviewed' | 'pending') => {
    if (!reviewModal) return;
    setReviewing(true);
    setReviewError('');
    try {
      await axios.put(
        `${API}/branch-config/drive-reports/${reviewModal.id}/status`,
        { status, review_notes: reviewNotes },
        { headers: authHeaders }
      );
      setReviewModal(null);
      setReviewNotes('');
      fetchReports();
    } catch (err: any) {
      setReviewError(err.response?.data?.error || err.message || 'Gagal mengubah status');
    } finally { setReviewing(false); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Hapus laporan ini?')) return;
    try {
      await axios.delete(`${API}/branch-config/drive-reports/${id}`, { headers: authHeaders });
      fetchReports();
    } catch { }
  };

  const fetchMedia = async () => {
    if (!selectedBranchId) return;
    try {
      const res = await axios.get(`${API}/branch-config/${selectedBranchId}`, { headers: authHeaders });
      setMediaFiles(res.data.data || []);
    } catch { setMediaFiles([]); }
  };

  const handleLogoUpload = async (file: File) => {
    if (!selectedBranchId || !file) return;
    setUploading(true);
    setUploadMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(
        `${API}/branch-config/media/${selectedBranchId}/logo`,
        fd,
        { headers: authHeaders }
      );
      setUploadMsg('✅ Logo berhasil diupload');
      fetchMedia();
    } catch (err: any) {
      setUploadMsg('❌ Gagal upload: ' + (err.response?.data?.error || err.message));
    } finally { setUploading(false); }
  };

  const handleDeleteMedia = async (mediaId: string) => {
    if (!window.confirm('Hapus file ini?')) return;
    try {
      await axios.delete(`${API}/branch-config/media/${mediaId}`, { headers: authHeaders });
      fetchMedia();
    } catch { }
  };

  const logoMedia = mediaFiles.find(m => m.media_type === 'logo');
  const logoUrl = selectedBranchId ? `${API}/branch-config/media/${selectedBranchId}/logo` : '';
  const pendingCount = reports.filter(r => r.status === 'pending').length;

  return (
    <div style={s.container}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, color: '#1e293b' }}>📋 Monitoring Cabang</h1>
          <p style={{ margin: '4px 0 0', color: '#666', fontSize: 13 }}>
            Terima laporan before/after dari cabang · Kelola logo & media
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div style={s.tabBar}>
        <button
          style={{ ...s.tab, ...(activeTab === 'laporan' ? s.tabActive : {}) }}
          onClick={() => setActiveTab('laporan')}
        >
          📋 Laporan Before/After
          {pendingCount > 0 && (
            <span style={s.badge}>{pendingCount}</span>
          )}
        </button>
        <button
          style={{ ...s.tab, ...(activeTab === 'logo' ? s.tabActive : {}) }}
          onClick={() => setActiveTab('logo')}
        >
          🖼️ Logo & Media Cabang
        </button>
      </div>

      {/* ===== TAB: LAPORAN BEFORE/AFTER ===== */}
      {activeTab === 'laporan' && (
        <div style={s.card}>
          {/* Filters */}
          <div style={s.filterRow}>
            <div style={s.filterGroup}>
              <label style={s.filterLabel}>Tanggal</label>
              <input
                type="date"
                value={filterDate}
                onChange={e => setFilterDate(e.target.value)}
                style={s.filterInput}
              />
            </div>
            <div style={s.filterGroup}>
              <label style={s.filterLabel}>Cabang</label>
              <select style={s.filterInput} value={filterBranch} onChange={e => setFilterBranch(e.target.value)}>
                <option value="all">Semua Cabang</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div style={s.filterGroup}>
              <label style={s.filterLabel}>Status</label>
              <select style={s.filterInput} value={filterStatus} onChange={e => setFilterStatus(e.target.value as any)}>
                <option value="all">Semua Status</option>
                <option value="pending">Belum Ditinjau</option>
                <option value="reviewed">Sudah Ditinjau</option>
              </select>
            </div>
            <button onClick={fetchReports} style={s.refreshBtn}>🔄 Refresh</button>
            <button
              onClick={() => setFilterDate(today)}
              style={{ ...s.refreshBtn, background: '#667eea', color: 'white' }}
            >
              📅 Hari Ini
            </button>
            <button
              onClick={() => setFilterDate('')}
              style={{ ...s.refreshBtn, background: '#64748b', color: 'white' }}
            >
              📋 Semua
            </button>
          </div>

          {/* Stats */}
          <div style={s.statsRow}>
            <div style={s.statBox}>
              <div style={s.statNum}>{reports.length}</div>
              <div style={s.statLabel}>Total Laporan</div>
            </div>
            <div style={{ ...s.statBox, borderColor: '#f39c12' }}>
              <div style={{ ...s.statNum, color: '#f39c12' }}>{pendingCount}</div>
              <div style={s.statLabel}>Belum Ditinjau</div>
            </div>
            <div style={{ ...s.statBox, borderColor: '#27ae60' }}>
              <div style={{ ...s.statNum, color: '#27ae60' }}>{reports.filter(r => r.status === 'reviewed').length}</div>
              <div style={s.statLabel}>Sudah Ditinjau</div>
            </div>
          </div>

          {/* Reports List */}
          {loadingReports ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Memuat laporan...</div>
          ) : reports.length === 0 ? (
            <div style={s.empty}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
              <div style={{ fontWeight: 600, color: '#555', marginBottom: 4 }}>
                Tidak ada laporan untuk filter ini
              </div>
              <div style={{ fontSize: 13, color: '#999' }}>
                Laporan akan muncul di sini setelah Admin Cabang mengirimkan via mobile app
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {reports.map(r => (
                <div key={r.id} style={{ ...s.reportCard, borderLeft: `4px solid ${r.status === 'reviewed' ? '#27ae60' : '#f39c12'}` }}>
                  <div style={s.reportHead}>
                    <div>
                      <span style={s.branchTag}>{r.branch_name}</span>
                      <span style={{ fontSize: 12, color: '#999', marginLeft: 8 }}>
                        {r.report_date ? new Date(r.report_date).toLocaleDateString('id-ID', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ ...s.statusBadge, background: r.status === 'reviewed' ? '#d4edda' : '#fff3cd', color: r.status === 'reviewed' ? '#155724' : '#856404' }}>
                        {r.status === 'reviewed' ? '✓ Sudah Ditinjau' : '⏳ Belum Ditinjau'}
                      </span>
                      <button onClick={() => { setReviewModal(r); setReviewNotes(r.review_notes || ''); setReviewError(''); }} style={s.reviewBtn}>
                        {r.status === 'reviewed' ? '📝 Edit Tinjauan' : '✅ Tinjau'}
                      </button>
                      <button onClick={() => handleDelete(r.id)} style={s.deleteBtn}>🗑️</button>
                    </div>
                  </div>

                  <div style={s.reportLinks}>
                    {r.before_drive_url && (
                      <a href={r.before_drive_url} target="_blank" rel="noopener noreferrer" style={s.driveLink}>
                        📷 Foto Before →
                      </a>
                    )}
                    {r.after_drive_url && (
                      <a href={r.after_drive_url} target="_blank" rel="noopener noreferrer" style={s.driveLink}>
                        📸 Foto After →
                      </a>
                    )}
                    {!r.before_drive_url && !r.after_drive_url && (
                      <span style={{ color: '#999', fontSize: 13 }}>Tidak ada link Drive</span>
                    )}
                  </div>

                  {r.notes && (
                    <div style={s.notes}>💬 {r.notes}</div>
                  )}
                  {r.review_notes && r.status === 'reviewed' && (
                    <div style={{ ...s.notes, background: '#d4edda', color: '#155724' }}>
                      ✓ Catatan tinjauan: {r.review_notes}
                    </div>
                  )}

                  <div style={{ fontSize: 11, color: '#aaa', marginTop: 8 }}>
                    Dikirim: {new Date(r.created_at).toLocaleString('id-ID')}
                    {r.submitted_by && ` · oleh ${r.submitted_by}`}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ===== TAB: LOGO & MEDIA ===== */}
      {activeTab === 'logo' && (
        <div style={s.card}>
          <div style={s.filterRow}>
            <div style={s.filterGroup}>
              <label style={s.filterLabel}>Pilih Cabang</label>
              <select style={s.filterInput} value={selectedBranchId} onChange={e => setSelectedBranchId(e.target.value)}>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name} — {b.city}</option>)}
              </select>
            </div>
          </div>

          {uploadMsg && (
            <div style={{ padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 13, background: uploadMsg.startsWith('✅') ? '#d4edda' : '#f8d7da', color: uploadMsg.startsWith('✅') ? '#155724' : '#721c24' }}>
              {uploadMsg}
            </div>
          )}

          <div style={s.logoSection}>
            <div style={s.logoPreview}>
              {logoMedia ? (
                <img
                  src={logoUrl}
                  alt="Logo cabang"
                  style={{ maxHeight: 160, maxWidth: '100%', objectFit: 'contain' }}
                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              ) : (
                <div style={{ color: '#aaa', textAlign: 'center' }}>
                  <div style={{ fontSize: 48 }}>🏢</div>
                  <div style={{ fontSize: 13, marginTop: 8 }}>Belum ada logo</div>
                </div>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: '0 0 8px', fontSize: 16, color: '#333' }}>Logo Cabang</h3>
              <p style={{ margin: '0 0 16px', fontSize: 13, color: '#666' }}>
                Logo ini akan tampil di semua struk/resi cabang ini. Format PNG/JPG, maks 5MB.
              </p>
              {logoMedia && (
                <div style={{ fontSize: 12, color: '#999', marginBottom: 12 }}>
                  File: {logoMedia.file_name} · {(logoMedia.file_size / 1024).toFixed(0)} KB
                  · Upload: {new Date(logoMedia.created_at).toLocaleDateString('id-ID')}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  style={{ padding: '9px 18px', background: '#667eea', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}
                >
                  {uploading ? 'Mengupload...' : logoMedia ? '🔄 Ganti Logo' : '📤 Upload Logo'}
                </button>
                {logoMedia && (
                  <button
                    onClick={() => handleDeleteMedia(logoMedia.id)}
                    style={{ padding: '9px 18px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}
                  >
                    🗑️ Hapus
                  </button>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={e => { const f = e.target.files?.[0]; if (f) handleLogoUpload(f); }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== REVIEW MODAL ===== */}
      {reviewModal && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <h3 style={{ margin: '0 0 6px', fontSize: 18 }}>✅ Tinjau Laporan</h3>
            <p style={{ margin: '0 0 16px', color: '#666', fontSize: 13 }}>
              {reviewModal.branch_name} · {reviewModal.report_date ? new Date(reviewModal.report_date).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {reviewModal.before_drive_url && (
                <a href={reviewModal.before_drive_url} target="_blank" rel="noopener noreferrer" style={s.driveLink}>📷 Buka Foto Before →</a>
              )}
              {reviewModal.after_drive_url && (
                <a href={reviewModal.after_drive_url} target="_blank" rel="noopener noreferrer" style={s.driveLink}>📸 Buka Foto After →</a>
              )}
            </div>
            <label style={{ fontSize: 13, fontWeight: 600, color: '#444', display: 'block', marginBottom: 6 }}>
              Catatan Tinjauan (opsional)
            </label>
            <textarea
              value={reviewNotes}
              onChange={e => setReviewNotes(e.target.value)}
              placeholder="Kondisi sudah baik / ada yang perlu diperbaiki..."
              style={{ width: '100%', padding: '9px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', minHeight: 80, boxSizing: 'border-box', resize: 'vertical' }}
            />
            {reviewError && (
              <div style={{ marginTop: 10, padding: '8px 12px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, color: '#dc2626', fontSize: 13 }}>
                {reviewError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button
                onClick={() => handleReview('reviewed')}
                disabled={reviewing}
                style={{ flex: 1, padding: '10px 0', background: '#27ae60', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}
              >
                {reviewing ? 'Menyimpan...' : '✅ Tandai Sudah Ditinjau'}
              </button>
              {reviewModal.status === 'reviewed' && (
                <button
                  onClick={() => handleReview('pending')}
                  disabled={reviewing}
                  style={{ flex: 1, padding: '10px 0', background: '#f39c12', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}
                >
                  ↩ Kembalikan ke Pending
                </button>
              )}
              <button
                onClick={() => { setReviewModal(null); setReviewError(''); }}
                style={{ padding: '10px 20px', background: '#f5f5f5', color: '#555', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  container: { padding: 20, background: '#f5f7fa', minHeight: '100vh' },
  header: { background: 'white', padding: 20, borderRadius: 8, marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  tabBar: { display: 'flex', gap: 4, marginBottom: 16, background: 'white', padding: '6px 8px', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' },
  tab: { padding: '9px 18px', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, background: 'transparent', color: '#555', display: 'flex', alignItems: 'center', gap: 8 },
  tabActive: { background: '#667eea', color: 'white', fontWeight: 700 },
  badge: { background: '#e74c3c', color: 'white', borderRadius: 20, padding: '1px 7px', fontSize: 11, fontWeight: 700 },
  card: { background: 'white', borderRadius: 8, padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: 16 },
  filterRow: { display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16 },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: 4 },
  filterLabel: { fontSize: 12, fontWeight: 600, color: '#555' },
  filterInput: { padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' },
  refreshBtn: { padding: '8px 14px', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: 6, cursor: 'pointer', fontSize: 13 },
  statsRow: { display: 'flex', gap: 12, marginBottom: 20 },
  statBox: { flex: 1, border: '2px solid #e2e8f0', borderRadius: 8, padding: '12px 16px', textAlign: 'center' },
  statNum: { fontSize: 28, fontWeight: 700, color: '#1e293b' },
  statLabel: { fontSize: 12, color: '#888', marginTop: 2 },
  empty: { textAlign: 'center', padding: '50px 20px', color: '#aaa' },
  reportCard: { border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, background: '#fafafa' },
  reportHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 },
  branchTag: { background: '#e8f0fe', color: '#1a56db', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 },
  statusBadge: { padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 },
  reviewBtn: { padding: '6px 12px', background: '#667eea', color: 'white', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 12 },
  deleteBtn: { padding: '6px 10px', background: '#fee2e2', color: '#c53030', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 13 },
  reportLinks: { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 8 },
  driveLink: { padding: '6px 14px', background: '#e8f0fe', color: '#1a56db', borderRadius: 6, fontSize: 13, textDecoration: 'none', fontWeight: 600 },
  notes: { fontSize: 13, color: '#555', background: '#f8fafc', padding: '8px 12px', borderRadius: 6, marginTop: 8 },
  logoSection: { display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' },
  logoPreview: { width: 200, height: 160, border: '2px dashed #ddd', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa', flexShrink: 0, overflow: 'hidden' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
  modal: { background: 'white', borderRadius: 10, padding: 28, width: '100%', maxWidth: 500, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
};
